import { useState, useCallback, useRef, useEffect } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { useDebounce } from "@/hooks/useDebounce";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Search,
  X,
  User,
  Scissors,
  Calendar,
  Phone,
  Clock,
  MessageCircle,
  UserRound,
  ChevronRight,
  Loader2,
  Zap,
  WalletCards,
  ClipboardList,
  CalendarPlus,
} from "lucide-react";

const STATUS_LABELS: Record<string, string> = {
  pending: "Pendente",
  confirmed: "Confirmado",
  completed: "Concluído",
  cancelled: "Cancelado",
  no_show: "Não compareceu",
};
const STATUS_COLORS: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  confirmed: "bg-blue-100 text-blue-800 border-blue-200",
  completed: "bg-green-100 text-green-800 border-green-200",
  cancelled: "bg-red-100 text-red-800 border-red-200",
  no_show: "bg-gray-100 text-gray-600 border-gray-200",
};

function formatTime(ts: Date | string | number) {
  const d = new Date(ts);
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }) + " " +
    d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function formatPrice(price: string | number | null | undefined) {
  if (price == null) return "";
  const n = Number(price);
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

interface GlobalSearchProps {
  open: boolean;
  onClose: () => void;
}

export function GlobalSearch({ open, onClose }: GlobalSearchProps) {
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 350);
  const inputRef = useRef<HTMLInputElement>(null);
  const [, navigate] = useLocation();
  const { data: staffCtx } = trpc.staff.myContext.useQuery();
  const staffRole = staffCtx?.staffRole ?? "admin";
  const trackExperience = trpc.experience.track.useMutation();

  const { data, isFetching } = (trpc as any).globalSearch.search.useQuery(
    { query: debouncedQuery },
    { enabled: debouncedQuery.length >= 2 }
  );

  const clients: any[] = data?.clients ?? [];
  const services: any[] = data?.services ?? [];
  const appointments: any[] = data?.appointments ?? [];
  const professionals: any[] = data?.professionals ?? [];
  const conversations: any[] = data?.conversations ?? [];
  const hasResults = clients.length > 0 || services.length > 0 || appointments.length > 0 || professionals.length > 0 || conversations.length > 0;
  const isEmpty = debouncedQuery.length >= 2 && !isFetching && !hasResults;

  // Auto-focus ao abrir
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      setQuery("");
    }
  }, [open]);

  function goToClient(client: any) {
    navigate("/clients");
    onClose();
  }

  function goToAppointment(appt: any) {
    navigate("/weekly-calendar");
    onClose();
  }

  function goToService() {
    navigate("/services");
    onClose();
  }

  function goToProfessional() {
    navigate("/professionals");
    onClose();
  }

  function goToConversation() {
    navigate("/ai-conversations");
    onClose();
  }

  const quickActions = staffRole === "funcionario"
    ? [
        { label: "Minha agenda", description: "Ver os atendimentos do turno", path: "/weekly-calendar", icon: Calendar, tone: "bg-amber-50 text-amber-700" },
        { label: "Bater ponto", description: "Registrar entrada ou saída", path: "/clock-in", icon: Clock, tone: "bg-violet-50 text-violet-700" },
        { label: "Minhas comissões", description: "Consultar os valores do período", path: "/my-commissions", icon: WalletCards, tone: "bg-emerald-50 text-emerald-700" },
      ]
    : staffRole === "caixa"
      ? [
          { label: "Receber pagamento", description: "Abrir o caixa do turno", path: "/checkout", icon: WalletCards, tone: "bg-emerald-50 text-emerald-700" },
          { label: "Ver agenda", description: "Localizar o atendimento", path: "/appointments", icon: Calendar, tone: "bg-amber-50 text-amber-700" },
          { label: "Bater ponto", description: "Registrar entrada ou saída", path: "/clock-in", icon: Clock, tone: "bg-violet-50 text-violet-700" },
        ]
      : staffRole === "recepcionista"
        ? [
            { label: "Novo agendamento", description: "Abrir a agenda para criar horário", path: "/weekly-calendar", icon: CalendarPlus, tone: "bg-amber-50 text-amber-700" },
            { label: "Receber pagamento", description: "Abrir atendimentos pendentes", path: "/checkout", icon: WalletCards, tone: "bg-emerald-50 text-emerald-700" },
            { label: "Ver clientes", description: "Consultar cadastro e histórico", path: "/clients", icon: User, tone: "bg-blue-50 text-blue-700" },
          ]
        : [
            { label: "Novo agendamento", description: "Abrir a agenda para criar horário", path: "/weekly-calendar", icon: CalendarPlus, tone: "bg-amber-50 text-amber-700" },
            { label: "Receber pagamento", description: "Abrir atendimentos pendentes", path: "/checkout", icon: WalletCards, tone: "bg-emerald-50 text-emerald-700" },
            { label: "Pendências de hoje", description: "Ver o que exige atenção agora", path: "/", icon: ClipboardList, tone: "bg-rose-50 text-rose-700" },
            { label: "Conversas IA", description: "Assumir atendimentos prioritários", path: "/ai-conversations", icon: MessageCircle, tone: "bg-emerald-50 text-emerald-700" },
          ];

  function runQuickAction(path: string) {
    trackExperience.mutate({ eventKey: "quick_action_opened", surface: "global_search" });
    navigate(path);
    onClose();
  }

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="bottom"
        className="rounded-t-2xl max-h-[92vh] flex flex-col p-0"
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-muted-foreground/25" />
        </div>

        {/* Search input */}
        <div className="px-4 pb-3 border-b">
          <div className="relative flex items-center">
            <Search className="absolute left-3 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar clientes, agenda, profissionais, serviços..."
              className="pl-9 pr-9 h-11 text-base bg-muted/50 border-0 focus-visible:ring-1"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-3 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto">
          {/* Loading */}
          {isFetching && debouncedQuery.length >= 2 && (
            <div className="flex items-center justify-center py-10 text-muted-foreground gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Buscando...</span>
            </div>
          )}

          {/* Empty state */}
          {isEmpty && (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-2">
              <Search className="w-8 h-8 opacity-30" />
              <p className="text-sm">Nenhum resultado para <strong>"{debouncedQuery}"</strong></p>
            </div>
          )}

          {/* Initial hint */}
          {query.length < 2 && !isFetching && (
            <div className="px-4 py-6">
              <div className="mb-5 rounded-xl border border-primary/15 bg-primary/5 p-3">
                <p className="flex items-center gap-2 text-sm font-medium"><Zap className="h-4 w-4 text-primary" />Ações rápidas para o seu turno</p>
                <p className="mt-0.5 text-xs text-muted-foreground">Escolha uma tarefa ou pesquise por cliente, agenda, profissional ou serviço.</p>
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {quickActions.map((action) => {
                    const Icon = action.icon;
                    return (
                      <button key={action.path} type="button" onClick={() => runQuickAction(action.path)} className="flex items-center gap-3 rounded-lg border border-border bg-background p-2.5 text-left transition-colors hover:border-primary/35 hover:bg-accent/30">
                        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${action.tone}`}><Icon className="h-4 w-4" /></span>
                        <span className="min-w-0"><span className="block text-sm font-medium">{action.label}</span><span className="block truncate text-xs text-muted-foreground">{action.description}</span></span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <p className="text-sm text-muted-foreground text-center mb-4">Ou digite pelo menos 2 caracteres para buscar</p>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {[
                  { icon: User, label: "Clientes", color: "text-blue-500", bg: "bg-blue-50" },
                  { icon: Scissors, label: "Serviços", color: "text-purple-500", bg: "bg-purple-50" },
                  { icon: Calendar, label: "Agendamentos", color: "text-amber-500", bg: "bg-amber-50" },
                  { icon: UserRound, label: "Profissionais", color: "text-rose-500", bg: "bg-rose-50" },
                  { icon: MessageCircle, label: "Conversas", color: "text-emerald-500", bg: "bg-emerald-50" },
                ].map(({ icon: Icon, label, color, bg }) => (
                  <div key={label} className={`flex flex-col items-center gap-2 p-3 rounded-xl ${bg}`}>
                    <Icon className={`w-5 h-5 ${color}`} />
                    <span className={`text-xs font-medium ${color}`}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Clients */}
          {clients.length > 0 && (
            <div className="px-4 pt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5" /> Clientes
                </span>
                <button
                  onClick={() => { navigate("/clients"); onClose(); }}
                  className="text-xs text-primary font-medium flex items-center gap-0.5"
                >
                  Ver todos <ChevronRight className="w-3 h-3" />
                </button>
              </div>
              <div className="space-y-1">
                {clients.map((c: any) => (
                  <button
                    key={c.id ?? c.name}
                    onClick={() => goToClient(c)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-accent/50 active:bg-accent transition-colors text-left"
                  >
                    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0 overflow-hidden">
                      {c.photoUrl
                        ? <img src={c.photoUrl} alt={c.name} className="w-full h-full object-cover" />
                        : <span className="text-sm font-bold text-primary">{(c.name ?? "?").charAt(0).toUpperCase()}</span>
                      }
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground truncate">{c.name}</p>
                      {c.phone && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                          <Phone className="w-3 h-3" /> {c.phone}
                        </p>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Services */}
          {services.length > 0 && (
            <div className="px-4 pt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                  <Scissors className="w-3.5 h-3.5" /> Serviços
                </span>
                <button
                  onClick={() => { navigate("/services"); onClose(); }}
                  className="text-xs text-primary font-medium flex items-center gap-0.5"
                >
                  Ver todos <ChevronRight className="w-3 h-3" />
                </button>
              </div>
              <div className="space-y-1">
                {services.map((s: any) => (
                  <button
                    key={s.id}
                    onClick={goToService}
                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-accent/50 active:bg-accent transition-colors text-left"
                  >
                    <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
                      <Scissors className="w-4 h-4 text-purple-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground truncate">{s.name}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-2">
                        <span className="flex items-center gap-0.5">
                          <Clock className="w-3 h-3" /> {s.durationMinutes}min
                        </span>
                        {s.price && <span className="font-medium text-primary">{formatPrice(s.price)}</span>}
                      </p>
                    </div>
                    {s.category && (
                      <Badge variant="outline" className="text-[10px] shrink-0">{s.category}</Badge>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Professionals */}
          {professionals.length > 0 && (
            <div className="px-4 pt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5"><UserRound className="w-3.5 h-3.5" /> Profissionais</span>
                <button onClick={goToProfessional} className="text-xs text-primary font-medium flex items-center gap-0.5">Ver equipe <ChevronRight className="w-3 h-3" /></button>
              </div>
              <div className="space-y-1">
                {professionals.map((professional: any) => (
                  <button key={professional.id} onClick={goToProfessional} className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-accent/50 active:bg-accent transition-colors text-left">
                    <div className="w-9 h-9 rounded-full bg-rose-100 flex items-center justify-center shrink-0 overflow-hidden">
                      {professional.avatarUrl ? <img src={professional.avatarUrl} alt={professional.name} className="w-full h-full object-cover" /> : <UserRound className="w-4 h-4 text-rose-600" />}
                    </div>
                    <div className="min-w-0 flex-1"><p className="text-sm font-medium text-foreground truncate">{professional.name}</p><p className="text-xs text-muted-foreground truncate">{professional.specialty || "Profissional"}</p></div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Appointments */}
          {appointments.length > 0 && (
            <div className="px-4 pt-4 pb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" /> Agendamentos
                </span>
                <button
                  onClick={() => { navigate("/weekly-calendar"); onClose(); }}
                  className="text-xs text-primary font-medium flex items-center gap-0.5"
                >
                  Ver agenda <ChevronRight className="w-3 h-3" />
                </button>
              </div>
              <div className="space-y-1">
                {appointments.map((a: any) => (
                  <button
                    key={a.id}
                    onClick={() => goToAppointment(a)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-accent/50 active:bg-accent transition-colors text-left"
                  >
                    <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                      <span className="text-sm font-bold text-amber-700">
                        {(a.clientName ?? "?").charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground truncate">{a.clientName ?? "Cliente"}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {a.serviceName} · {formatTime(a.startTime)}
                      </p>
                    </div>
                    <Badge
                      variant="outline"
                      className={`text-[10px] shrink-0 ${STATUS_COLORS[a.status] ?? ""}`}
                    >
                      {STATUS_LABELS[a.status] ?? a.status}
                    </Badge>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Conversations — visible only when the backend grants access */}
          {conversations.length > 0 && (
            <div className="px-4 pt-4 pb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5"><MessageCircle className="w-3.5 h-3.5" /> Conversas IA</span>
                <button onClick={goToConversation} className="text-xs text-primary font-medium flex items-center gap-0.5">Ver conversas <ChevronRight className="w-3 h-3" /></button>
              </div>
              <div className="space-y-1">
                {conversations.map((conversation: any) => (
                  <button key={conversation.sessionId} onClick={goToConversation} className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-accent/50 active:bg-accent transition-colors text-left">
                    <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center shrink-0"><MessageCircle className="w-4 h-4 text-emerald-600" /></div>
                    <div className="min-w-0 flex-1"><p className="text-sm font-medium text-foreground truncate">{conversation.preview}</p><p className="text-xs text-muted-foreground">{formatTime(conversation.createdAt)}</p></div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// Botão de trigger para o header
export function GlobalSearchTrigger({ className }: { className?: string }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, []);
  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className={`h-8 w-8 text-muted-foreground hover:text-foreground ${className ?? ""}`}
        onClick={() => setOpen(true)}
        title="Busca e ações rápidas (Ctrl+K)"
        aria-label="Abrir busca e ações rápidas"
        aria-keyshortcuts="Control+K Meta+K"
      >
        <Search className="w-4 h-4" />
      </Button>
      <GlobalSearch open={open} onClose={() => setOpen(false)} />
    </>
  );
}
